/*
    This file is part of Universal Sequences.
    Universal Sequences is free software: you can redistribute it and/or modif
    it under the terms of the GNU General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.

    Universal Sequences is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU General Public License for more details.

    You should have received a copy of the GNU General Public License
    along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import {
    INSTRUMENT_RACK,
    MULTI_FREEZE
} from 'constants/track-types.js';

const sliceColors = ['#29FFFF', '#5429FF', '#FF29D0', '#0AFF6C', '#FFBA7B', '#FFE600', '#8FFF00', '#9EF3FF', '#A48DFF', '#FF9AE9', '#FF8D8D', '#FF7A00', '#C8FF82' ];

const MIN_BRIGHTNESS = 170;
const brightCache = {};
const trackCache = {};

class Colors {
    constructor() {
    }

    getColorForTrack(track) {
        if (track === undefined) {
            return this.getColorForHash(undefined);
        }

        if (track.envelopesTrack) {
            return this.getColorForHash(track.presetName || track.id);
        }

        if (track.getTrackType() === INSTRUMENT_RACK) {
            track = (track.tracks[0] && track.tracks[0].track) || track;
        }
        let id = track.sample !== undefined && track.sample.getId() !== undefined ?
            track.sample.getId() :
            track.presetName ||
            (track.instrument &&
             track.instrument.defaultSampler &&
             track.instrument.defaultSampler.sample.getId()) || track.getName();

        if (track.isAudioTrack) {
            id = track.id;
        }

        if (track.getTrackType() === MULTI_FREEZE) {
            id = track.presetName || "MULTI FREEZE";
        }

        if (id in trackCache) {
            return trackCache[id];
        }
        let trackColor = this.getColorForHash(id);
        let color = this.saturate(trackColor, 4);
        trackCache[id] = color;
        return color;
    }

    transparent(color, opacity) {
        return color.slice(0, color.length-1) + ', ' + opacity + ')';
    }

    getColor(i) {
        return sliceColors[i % sliceColors.length];
    }

    getColorForHash(hash, minBrightness=MIN_BRIGHTNESS) {
        if (hash === "Empty Patch") {
            return "#fb1515";
        } else if (hash === 'Synth') {
        
            return '#ff0471';
        } else if (hash === 'External Instrument') {
            return '#7effb3';
        } else if (hash === 'Resampled') {
            return '#ff1e00';
        } else if (hash === 'breaks' || hash === 'drums') {
            return '#11ff64';
        }
        let color = this.intToRGB(this.hashCode(hash));
        let rgb = this.getRGB(color);
        let {h, s, l} = RGBToHSL(rgb);
        s = 100;
        l = 60;
        return this.rawHSLToHex({h,s,l});
    }

    hashCode(str="") { // java String#hashCode
        var hash = 0;

        for (var i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        return hash;
    } 

    intToRGB(i){
        var c = (i & 0x00FFFFFF)
            .toString(16)
            .toUpperCase();
        
        return "#" + "00000".substring(0, 6 - c.length) + c;
    }

    hueShift(c, shift) {
        let {h, s, l} = getHSLFromString(c);
        return getHSLString({h: h + shift, s, l});
    }

    compliment(c, rotate=2) {
        let {h, s, l} = getHSLFromString(c);
        if (h === 0) {
            h++;
        }
        h += rotate*10 % 255;
        let x = getHSLString({h, s, l});
        return x;
    }

    saturate(c, factor, lightFactor=1) {
        if (!c) {
            return undefined;
        }
        if (c.startsWith("hsl")) {
            let {h, s, l} = getHSLFromString(c);
            l *= lightFactor;
            s = Math.min(s*factor, 100);
            c = getHSLString({h, s, l});
            return c;
        } else {
            let rgb = this.getRGB(c);
            let {h, s, l} = RGBToHSL(rgb);
            l *= lightFactor;
            s = Math.min(s*factor, 100);
            c = getHSLString({h, s, l});
            return c;
        }
    }

    brighten(c, factor) {
        if (c.startsWith("hsl")) {
            let hsl = getHSLFromString(c);
            hsl = brightenHSL(hsl, factor);
            return getHSLString(hsl);
        }
        let cacheResult = brightCache[(c + '.' + factor)];
        if (cacheResult) {
            return cacheResult;
        }
        let r = Math.min(255, Math.floor(parseInt(c.slice(1, 3), 16) * factor));
        let g = Math.min(255, Math.floor(parseInt(c.slice(3, 5), 16) * factor));
        let b = Math.min(255, Math.floor(parseInt(c.slice(5), 16) * factor));

        r = r.toString(16);
        g = g.toString(16);
        b = b.toString(16);

        if (r.length === 1) {
            r = '0' + r;
        }

        if (g.length === 1) {
            g = '0' + g;
        }

        if (b.length === 1) {
            b = '0' + b;
        }

        let x = '#' + r + g + b;
        brightCache[c + '.' + factor] = x;
        return x;

    }

    getBrightness(c) {
        let rgb = this.getRGB(c);

        let color = 0.299*rgb.r + 0.587*rgb.g + 0.114*rgb.b;
        return color;
    }

    rawHSLToHex({h,s,l}) {
        return hslToHex(h,s,l);
    }

    hslToHex(color) {
        let {h,s,l} = getHSLFromString(color);
        return hslToHex(h,s,l);
    }

    getRGB(c) {
        let r = Math.floor(parseInt(c.slice(1, 3), 16)); 
        let g = Math.floor(parseInt(c.slice(3, 5), 16));
        let b = Math.floor(parseInt(c.slice(5), 16));
        return {r:r, g:g, b:b};
    }
}

function RGBToHSL({r,g,b}) {
    // Make r, g, and b fractions of 1
    r /= 255;
    g /= 255;
    b /= 255;

    // Find greatest and smallest channel values
    let cmin = Math.min(r,g,b),
        cmax = Math.max(r,g,b),
        delta = cmax - cmin,
        h = 0,
        s = 0,
        l = 0;

    if (delta == 0)
        h = 0;
    // Red is max
    else if (cmax == r)
        h = ((g - b) / delta) % 6;
    // Green is max
    else if (cmax == g)
        h = (b - r) / delta + 2;
    // Blue is max
    else
        h = (r - g) / delta + 4;

    h = Math.round(h * 60);

    // Make negative hues positive behind 360°
    if (h < 0)
        h += 360;

    // Calculate lightness
    l = (cmax + cmin) / 2;

    // Calculate saturation
    s = delta == 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

    // Multiply l and s by 100
    s = +(s * 100).toFixed(1);
    l = +(l * 100).toFixed(1);

    return {h, s, l};
}

function getHSLString({h, s, l}) {
    return "hsl(" + h + "," + s + "%," + l +"%)";
}

function getHSLFromString(str) {
    str = str.substring(4);
    let [hPart, sPart, lPart] = str.split(",");
    let index = sPart.indexOf("%");
    sPart = sPart.substring(0, index);
    index = lPart.indexOf("%");
    lPart = lPart.substring(0, index);
    return {
        h: parseFloat(hPart),
        s: parseFloat(sPart),
        l: parseFloat(lPart),
    };

}

function brightenHSL({h, s, l}, factor) {
    return {
        h,
        s,
        l: l* factor
    };
}

function hslToHex(h, s, l) {
  l /= 100;
  const a = s * Math.min(l, 1 - l) / 100;
  const f = n => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');   // convert to Hex and prefix "0" if needed
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}



export default Colors;
